import React, { useContext } from 'react';
import { CollapseCard, Button } from '@totalsoft/rocket-ui';
import Assignment from '@mui/icons-material/Assignment';
import { Box } from '@mui/material';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import FinancedOffersListItemComponent from './FinancedOffersListItemComponent';
import { compose } from 'redux';
import { RightsContext } from 'providers/RightsContext';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { FinancedOffersWarningContainer } from '../../FolderGeneralDataStyles';
import { GridActionsWrapper } from 'components/common/CommonStyles';

export const FinancedOffersListComponent = ({
  t,
  financedOffers,
  offerPartners,
  openFinancedOffer,
  onAddFinancedOffers,
  openOfferClientQuestionnaire,
  onRemoveFinancedOffers,
  isSelectionMode,
  selectAllClientOffers,
  isLoaded,
  userInfo,
  updateFinancedOffersOnFolder,
  isRemoveOffersMode,
  handleClientOfferSelection,
  openFolder,
  openMasterPartner,
  onBlockFacilitySublimitAmountManually,
  displayAssociatedPartners,
  masterPartnerApp
}) => {
  const { isImage } = useContext(RightsContext);
  const { riskFolderWithMultipleOffer } = useContext(ApplicationFeaturesContext);
  const canAddNewFolder = userInfo && userInfo.permisionNewFolder;

  return (
    <CollapseCard
      icon={Assignment}
      title={
        isSelectionMode
          ? isRemoveOffersMode
            ? t('Folder.GeneralData.FinancedOffers.RemoveFinancedOffersTitle')
            : t('Folder.GeneralData.FinancedOffers.AddClientOffersTitle')
          : t('Folder.GeneralData.FinancedOffers.Title')
      }
      defaultExpanded
      toggleOnHeaderClick
    >
      <div>
        {isImage && (
          <FinancedOffersWarningContainer>
            <WarningRoundedIcon />
            {t('Folder.GeneralData.FinancedOffers.FinancedOffersMessage')}
          </FinancedOffersWarningContainer>
        )}

        {financedOffers.map((offer, _index) => {
          return (
            <FinancedOffersListItemComponent
              key={offer.offerId}
              clientName={offer.clientName}
              offerNumber={offer.documentNumber}
              offerId={offer.offerId}
              offerSiteId={offer.offerSiteId}
              currentOfferId={offer.currentOfferId}
              currentOfferSiteId={offer.currentOfferSiteId}
              offerDate={offer.offerDate}
              offerStateName={offer.offerStateName}
              financedAmount={offer.financedAmount}
              offerFinancialProduct={offer.financialProductName}
              offerRezidualPercent={offer.rezidualPercent}
              offerBaloonPercent={offer.baloonPercent}
              offerShowBaloon={offer.showBaloon}
              contractRezidualPercent={offer.contractRezidualPercent}
              remainingDuration={offer.remainingDuration}
              contractDuration={offer.contractDuration}
              offerAdvancePercent={offer.advancePercent}
              offerCellParametrization={offer.hasCellParametrizationYN}
              offerDuration={offer.duration}
              currency={offer.currency}
              assets={offer.assets}
              openFinancedOffer={openFinancedOffer}
              openFolder={openFolder}
              openMasterPartner={openMasterPartner}
              openOfferClientQuestionnaire={openOfferClientQuestionnaire}
              onBlockFacilitySublimitAmountManually={onBlockFacilitySublimitAmountManually}
              masterPartnerApp={masterPartnerApp}
              isSelectionMode={isSelectionMode}
              selected={offer.selected}
              offerPartners={offerPartners.filter(x => x.offerId === offer.currentOfferId)}
              displayAssociatedPartners={displayAssociatedPartners}
              onChange={handleClientOfferSelection(offer.offerId)}
              outstandingAmount={offer.outstandingAmount}
              documentTypeId={offer.documentTypeId}
              uninvoicedAdvance={offer.uninvoicedAdvance}
              hasERPFacility={Boolean(offer.clientFacilityId)}
              facilityNumber={offer.facilityNumber}
              isFacilityBlocked={offer.isFacilityBlocked}
              webFacilityId={offer.facilityId}
              webFacilitySiteId={offer.facilitySiteId}
            />
          );
        })}

        {isImage ? null : isSelectionMode ? (
          isLoaded && financedOffers.length === 0 ? (
            <Box>{t('Folder.GeneralData.FinancedOffers.NoOffersFoundForClient')}</Box>
          ) : (
            <GridActionsWrapper>
              <Button
                onClick={updateFinancedOffersOnFolder()}
                disabled={financedOffers.length > 0 && financedOffers.every(x => !x.selected)}
              >
                {t('Folder.GeneralData.FinancedOffers.Save')}
              </Button>
              <Button onClick={selectAllClientOffers()}>{t('Folder.GeneralData.FinancedOffers.SelectAllClientOffers')}</Button>
            </GridActionsWrapper>
          )
        ) : (
          <GridActionsWrapper>
            {riskFolderWithMultipleOffer && (
              <Button onClick={onAddFinancedOffers()}>{t('Folder.GeneralData.FinancedOffers.AddClientOffers')}</Button>
            )}
            {riskFolderWithMultipleOffer && canAddNewFolder && (
              <Button variant='outlined' onClick={onRemoveFinancedOffers()}>
                {t('Folder.GeneralData.FinancedOffers.RemoveClientOffers')}
              </Button>
            )}
          </GridActionsWrapper>
        )}
      </div>
    </CollapseCard>
  );
};

FinancedOffersListComponent.propTypes = {
  financedOffers: PropTypes.array.isRequired,
  offerPartners: PropTypes.array.isRequired,
  openFinancedOffer: PropTypes.func.isRequired,
  openFolder: PropTypes.func.isRequired,
  openMasterPartner: PropTypes.func.isRequired,
  openOfferClientQuestionnaire: PropTypes.func.isRequired,
  updateFinancedOffersOnFolder: PropTypes.func.isRequired,
  onAddFinancedOffers: PropTypes.func.isRequired,
  onRemoveFinancedOffers: PropTypes.func.isRequired,
  isRemoveOffersMode: PropTypes.bool.isRequired,
  handleClientOfferSelection: PropTypes.func,
  selectAllClientOffers: PropTypes.func,
  onBlockFacilitySublimitAmountManually: PropTypes.func,
  isLoaded: PropTypes.bool,
  isSelectionMode: PropTypes.bool.isRequired,
  masterPartnerApp: PropTypes.bool,
  displayAssociatedPartners: PropTypes.bool,
  t: PropTypes.func.isRequired,
  userInfo: PropTypes.object
};

export default compose(withTranslation())(FinancedOffersListComponent);
